import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/auth';
import { Tabs } from '../components/ui/Tabs';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

type Tab = 'phone' | 'email';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);

  const handleSendSms = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      alert(t('auth.forgotPassword.phoneInvalid'));
      return;
    }
    await authApi.sendSms({ phone, scene: 'reset_password' });
    setSmsCooldown(60);
    const timer = setInterval(() => {
      setSmsCooldown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const handleSubmit = async () => {
    if (newPassword.length < 8) {
      alert(t('auth.forgotPassword.passwordTooShort'));
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword({
        phone: tab === 'phone' ? phone : undefined,
        email: tab === 'email' ? email : undefined,
        smsCode: tab === 'phone' ? smsCode : undefined,
        newPassword,
      });
      setDone(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || t('auth.forgotPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          <h1 className="font-display text-3xl mb-4">{t('auth.forgotPassword.successTitle')}</h1>
          <p className="text-daynote-muted text-sm mb-8">{t('auth.forgotPassword.successSubtitle')}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-daynote-accent text-[#2a2520] rounded-lg font-medium text-sm"
          >
            {t('auth.forgotPassword.successBackToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 relative">
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-[400px]">
        <Link to="/login" className="text-sm text-daynote-muted hover:text-daynote-text mb-8 inline-block">
          {t('auth.forgotPassword.backToLogin')}
        </Link>
        <h1 className="font-display text-4xl font-medium mb-2">{t('auth.forgotPassword.title')}</h1>
        <p className="text-[13px] text-daynote-muted mb-8">{t('auth.forgotPassword.subtitle')}</p>

        <Tabs
          items={[
            { id: 'phone' as Tab, label: t('auth.forgotPassword.tabPhone') },
            { id: 'email' as Tab, label: t('auth.forgotPassword.tabEmail') },
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === 'phone' ? (
          <div className="space-y-4">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('auth.forgotPassword.phonePlaceholder')}
              className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
            />
            <div className="relative">
              <input
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                placeholder={t('auth.forgotPassword.smsPlaceholder')}
                className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 pr-24 text-sm focus:outline-none focus:border-daynote-accent"
              />
              <button
                type="button"
                disabled={smsCooldown > 0}
                onClick={handleSendSms}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs text-daynote-accent border border-daynote-accent rounded-md disabled:opacity-40 bg-transparent cursor-pointer"
              >
                {smsCooldown > 0 ? `${smsCooldown}s` : t('auth.forgotPassword.sendSms')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.forgotPassword.emailPlaceholder')}
              className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
            />
            <p className="text-xs text-daynote-muted">
              {t('auth.forgotPassword.emailDevNote')}
            </p>
          </div>
        )}

        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t('auth.forgotPassword.newPasswordPlaceholder')}
          className="w-full mt-4 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
        />

        <button
          type="button"
          disabled={loading}
          onClick={handleSubmit}
          className="w-full mt-6 py-4 bg-daynote-accent text-[#2a2520] rounded-lg font-semibold text-sm border-none cursor-pointer disabled:opacity-60"
        >
          {loading ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
        </button>
      </div>
    </div>
  );
}
